import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Button } from "../components/ui/button";
import BottomNav from "../components/BottomNav";
import { 
  Bell, ArrowLeft, Store, User, Loader2, Check, Trash2, 
  CheckCheck, Megaphone, UserPlus, Clock
} from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Capacitor } from "@capacitor/core";
import { PushNotifications } from "@capacitor/push-notifications";

// Clear iOS/Android badge count
const clearAppBadge = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      await PushNotifications.removeAllDeliveredNotifications();
      // Reset badge count to 0
      if (Capacitor.getPlatform() === 'ios') {
        // For iOS, we need to set badge to 0
        await PushNotifications.setBadgeCount({ count: 0 });
      }
    } catch (e) {
      console.log("Badge clear error:", e);
    }
  }
};

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      const unread = response.data.unread_count || 0;
      setUnreadCount(unread);
      // If no unread notifications, clear the app badge
      if (unread === 0) {
        await clearAppBadge();
      }
    } catch (e) {
      console.error("Error fetching notifications:", e);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  const handleMarkRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) {
      toast.error("Failed to mark as read");
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/mark-all-read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      await clearAppBadge(); // Clear iOS/Android badge
      toast.success("All notifications marked as read");
    } catch (e) {
      toast.error("Failed to mark all as read");
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await axios.delete(`${API}/notifications/${notificationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success("Notification deleted");
    } catch (e) {
      toast.error("Failed to delete notification");
    }
  };

  const handleClearAll = async () => {
    try {
      await axios.delete(`${API}/notifications/clear-all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
      setUnreadCount(0);
      toast.success("All notifications cleared");
    } catch (e) {
      toast.error("Failed to clear notifications");
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "vendor_promo":
        return <Megaphone className="w-5 h-5 text-primary" />;
      case "new_follower":
        return <UserPlus className="w-5 h-5 text-green-500" />;
      case "follow_request":
        return <UserPlus className="w-5 h-5 text-yellow-500" />;
      case "venue_update":
        return <Store className="w-5 h-5 text-blue-500" />;
      default:
        return <Bell className="w-5 h-5 text-white/60" />;
    }
  };

  const handleNotificationClick = (notification) => {
    // Mark as read
    if (!notification.read) {
      handleMarkRead(notification.id);
    }
    
    // Navigate based on type
    if (notification.sender_type === "user" && notification.sender_id) {
      navigate(`/u/${notification.sender_id}`);
    } else if (notification.sender_type === "location" && notification.sender_id) {
      navigate(`/venue/${notification.sender_id}`);
    } else if (notification.sender_type === "vendor" && notification.sender_id) {
      // Navigate to vendor's first location or master page
      navigate(`/venue/${notification.sender_id}`);
    }
  };

  const formatTime = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return "";
    }
  };

  return (
    <div className="min-h-screen bg-background pb-20" data-testid="notifications-page">
      {/* Header */}
      <div className="sticky top-0 z-10 glass-card border-b border-white/10 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="text-white/60 hover:text-white" data-testid="back-btn">
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div>
              <h1 className="text-lg font-semibold text-white flex items-center gap-2">
                Notifications
                {unreadCount > 0 && (
                  <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                    {unreadCount}
                  </span>
                )}
              </h1>
              <p className="text-white/50 text-xs">Stay updated with activity</p>
            </div>
          </div>
          
          {notifications.length > 0 && (
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleMarkAllRead}
                  className="text-primary hover:text-primary/80"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-4 h-4" />
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="text-red-500 hover:text-red-400"
                data-testid="clear-all-btn"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : notifications.length === 0 ? (
          <div className="text-center py-12">
            <Bell className="w-16 h-16 text-white/20 mx-auto mb-4" />
            <p className="text-white/60 font-medium">No notifications yet</p>
            <p className="text-white/40 text-sm mt-1">
              You'll see updates from venues and followers here
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`glass-card p-4 cursor-pointer transition-all ${
                  !notification.read ? "border-l-4 border-primary" : ""
                }`}
                onClick={() => handleNotificationClick(notification)}
                data-testid={`notification-${notification.id}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    {getNotificationIcon(notification.notification_type)}
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className={`font-medium ${!notification.read ? "text-white" : "text-white/70"}`}>
                          {notification.title}
                        </h3>
                        {notification.sender_name && (
                          <p className="text-primary text-sm">{notification.sender_name}</p>
                        )}
                      </div>
                      <span className="text-white/40 text-xs whitespace-nowrap flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {formatTime(notification.created_at)}
                      </span>
                    </div>
                    
                    <p className={`text-sm mt-1 ${!notification.read ? "text-white/80" : "text-white/50"}`}>
                      {notification.message}
                    </p>
                  </div>
                </div>

                {/* Action buttons */}
                <div className="flex justify-end gap-2 mt-3 pt-2 border-t border-white/5">
                  {!notification.read && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMarkRead(notification.id);
                      }}
                      className="text-white/60 hover:text-white text-xs"
                    >
                      <Check className="w-3 h-3 mr-1" />
                      Mark read
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDelete(notification.id);
                    }}
                    className="text-red-500/60 hover:text-red-500 text-xs"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default NotificationsPage;
